import AOS from "aos";
import "aos/dist/aos.css";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Cursor from "../../../common/Cursor";
import Footer from "../../../common/Footer";
import Header from "../../../common/Header";
import Loader from "../../../common/Loader";
import ProgressCircle from "../../../common/ProgressCircle";
import TextMarqueSection from "../../../common/TextMarqueSection";
import SectionTwo from "../SectionTwo";
import { Helmet } from "react-helmet";
import { fetchData, toAbsoluteMedia } from "../../../api/api";

const SingleBlog = () => {
  const { slug } = useParams();

  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 2000 });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        // Detail endpoint by slug (with trailing slash)
        const data = await fetchData(`/blog/blogs/${encodeURIComponent(slug)}/`);

        const mapped = {
          slug: data.slug || "",
          title: data.title || "",
          author: data.author || "",
          category: data?.category?.name || "",
          // handle either created_at or published_at or none
          created_at: data.created_at || data.published_at || null,
          image: toAbsoluteMedia(data.image || ""),
          description: data.description || "", // HTML
        };

        if (alive) setPost(mapped);
      } catch {
        if (alive) setErr("Blog post not found.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) return <Loader />;
  if (err || !post) {
    return <div style={{ textAlign: "center", padding: 40, color: "#fff" }}>{err || "Blog post not found"}</div>;
  }

  return (
    <div className="site_contain">
     <Helmet>
  <title>{post.title} | Marichi Tech AI</title>
  <meta name="description" content={post.description?.replace(/<[^>]+>/g, "").slice(0, 160)} />

  {/* Open Graph for Facebook & LinkedIn */}
  <meta property="og:type" content="article" />
  <meta property="og:url" content={`https://marichitechai.com/blog/${post.slug}`} />
  <meta property="og:title" content={post.title} />
  <meta property="og:description" content={post.description?.replace(/<[^>]+>/g, "").slice(0, 160)} />
  <meta property="og:image" content={post.image} />

  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={post.title} />
  <meta name="twitter:description" content={post.description?.replace(/<[^>]+>/g, "").slice(0, 160)} />
  <meta name="twitter:image" content={post.image} />
</Helmet>

      <Cursor />
      <Header />

      {/* Header */}
      <section style={{ padding: "40px 0", overflow: "hidden" }}>
        <div>
          <p
            style={{ textAlign: "center", fontSize: 16, color: "#6b7280", textTransform: "uppercase", marginBottom: 16 }}
            data-aos="fade-down"
          >
            {post.category || "Blog"}
          </p>
          <h1
            style={{ fontSize: "clamp(24px, 5vw, 36px)", fontWeight: "bold", color: "white", textAlign: "center", marginBottom: 8 }}
            data-aos="fade-down"
          >
            {post.title}
          </h1>
          <p style={{ textAlign: "center", color: "#9ca3af", marginTop: 0 }} data-aos="fade-up">
            {post.author ? `by ${post.author}` : ""}
            {post.created_at ? ` • ${new Date(post.created_at).toLocaleDateString()}` : ""}
          </p>
        </div>
      </section>

      {/* Image + Content */}
      <section style={{ padding: "40px 0" }}>
        <div>
          {post.image && (
            <div style={{ marginBottom: 24, borderRadius: 12, overflow: "hidden" }} data-aos="fade-up">
              <img
                src={post.image}
                alt={post.title}
                style={{ width: "100%", height: 300, objectFit: "cover", borderRadius: 12 }}
                onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/900x300?text=Blog")}
              />
            </div>
          )}

          {/* Renders the HTML body */}
          <SectionTwo post={post} />
        </div>
      </section>

      <TextMarqueSection />
      <Footer />
      <ProgressCircle />
    </div>
  );
};

export default SingleBlog;
